import React, { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { LEARN } from "../../theme";
import { COUNTING_ENGINE_THEME, countingSpokenWord, countingTarget } from "../../../../data/countingLadderCatalog";
import { useDropTarget } from "../../interaction/useDragEngine";
import { ConfettiBurst } from "../../feedback/Celebration";
import {
  CornerPill, CountingPanel, CountingPiece, CountingRoot, DRAG_BUTTON, PanelChip, POP_SPRING, SupplyShelf, hexAlpha, trayPieceSizeCss,
  useCompleteOnce, useCountingDrag,
} from "../CountingPiece";

const BASKET = "basket";
const BASKET_DATA = { kind: "basket" };

/**
 * Give-N: drag exactly the asked-for number of blueberries into the basket
 * (tap a berry, then tap the basket, also works). Berries stack inside the
 * basket; tapping one in the basket takes it back out.
 */
const GiveNGame = ({ activity, speak, sfx, onComplete, reduceMotion = false }) => {
  const target = countingTarget(activity);
  const object = activity.number === 26 ? "crayon" : "blueberry";
  const noun = object === "crayon" ? "crayons" : "blueberries";
  const accent = COUNTING_ENGINE_THEME.giveN.accent;
  const supply = useMemo(() => Array.from({ length: Math.min(target + 3, 9) }, (_, i) => i), [target]);
  const [inBasket, setInBasket] = useState([]);
  const [selected, setSelected] = useState(null);
  const [burst, setBurst] = useState(0);
  const complete = useCompleteOnce(onComplete);
  const done = inBasket.length === target;

  const place = (id) => {
    if (done || inBasket.includes(id)) return false;
    const next = [...inBasket, id];
    setInBasket(next);
    setSelected(null);
    sfx?.("snap");
    speak?.(countingSpokenWord(next.length), { interrupt: true });
    if (next.length === target) {
      setTimeout(() => sfx?.("correct"), 140);
      setBurst((b) => b + 1);
      complete();
    }
    return true;
  };

  const takeOut = (id) => {
    if (done) return;
    setInBasket((list) => list.filter((x) => x !== id));
    sfx?.("pop", { rate: 0.9 });
  };

  const { dragProps, registerTarget, hoverId, dragging, guardTap, GhostLayer } = useCountingDrag({
    reduceMotion,
    sfx,
    onDrop: ({ payload, targetId }) => (targetId === BASKET ? place(payload) : false),
  });
  const basketRef = useDropTarget(registerTarget, BASKET, BASKET_DATA);
  const hovering = hoverId === BASKET;
  const armed = hovering || (selected != null && !dragging);

  const ghost = () => <CountingPiece asset={object} variant="bare" size="100%" />;
  const traySize = trayPieceSizeCss(supply.length);

  return (
    <CountingRoot>
      <CountingPanel
        grow
        tint={accent}
        chip={<PanelChip lead="Pack" value={target} text={noun} accent={accent} />}
        corner={<CornerPill>{inBasket.length} of {target}</CornerPill>}
        style={{ justifyContent: "flex-end", minHeight: "calc(var(--tile) * 2.6)" }}
      >
        <motion.div
          ref={basketRef}
          role="button"
          tabIndex={0}
          aria-label={`Basket, ${inBasket.length} inside`}
          aria-disabled={done ? "true" : undefined}
          onClick={() => { if (selected != null) place(selected); }}
          onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && selected != null) { e.preventDefault(); place(selected); } }}
          className="counting-btn counting-target"
          animate={reduceMotion ? {} : { scale: hovering ? 1.06 : armed ? 1.03 : 1, y: hovering ? -6 : 0 }}
          transition={{ type: "spring", stiffness: 380, damping: 24 }}
          style={{
            position: "relative", height: "min(calc(var(--tile) * 4.2), 100%)", maxWidth: "92%", aspectRatio: "1 / 1", flexShrink: 1, minHeight: 0,
            display: "grid", placeItems: "center", cursor: selected != null ? "pointer" : "default", borderRadius: "28%", "--target-accent": hexAlpha(accent, 0.6),
            outline: armed ? `4px solid ${hexAlpha(accent, hovering ? 0.9 : 0.45)}` : "4px solid transparent",
            outlineOffset: 6, transition: "outline-color 140ms", background: armed ? hexAlpha(accent, hovering ? 0.16 : 0.08) : "transparent",
          }}
        >
          {/* landing glow under the basket */}
          <span
            aria-hidden="true"
            style={{
              position: "absolute", left: "10%", right: "10%", bottom: "2%", height: "12%", borderRadius: "50%",
              background: `radial-gradient(ellipse at center, ${hexAlpha(LEARN.color.ink, hovering ? 0.28 : 0.18)}, transparent 70%)`,
              filter: "blur(4px)", transition: "opacity 200ms", zIndex: 0,
            }}
          />
          <CountingPiece asset="basket" variant="bare" size="100%" lifted={hovering} reduceMotion={reduceMotion} style={{ zIndex: 1 }} />
          <div
            aria-hidden="true"
            style={{
              position: "absolute", left: "17%", right: "17%", top: "24%", height: "46%", zIndex: 2,
              display: "flex", flexWrap: "wrap-reverse", alignContent: "flex-start", justifyContent: "center", gap: "1%",
            }}
          >
            <AnimatePresence>
              {inBasket.map((id, i) => (
                <motion.button
                  key={id}
                  type="button"
                  className="counting-btn"
                  aria-label={`${object} ${i + 1} in the basket, tap to take out`}
                  onClick={(e) => { e.stopPropagation(); takeOut(id); }}
                  initial={reduceMotion ? false : { scale: 0.4, y: -30, opacity: 0 }}
                  animate={{ scale: 1, y: 0, opacity: 1, rotate: ((i % 3) - 1) * 10 }}
                  exit={reduceMotion ? { opacity: 0 } : { scale: 0.4, opacity: 0 }}
                  transition={reduceMotion ? { duration: 0 } : POP_SPRING}
                  style={{ ...DRAG_BUTTON, padding: 0, cursor: done ? "default" : "pointer", width: "24%", aspectRatio: "1 / 1", borderRadius: "50%" }}
                >
                  <CountingPiece asset={object} variant="bare" size="100%" reduceMotion={reduceMotion} />
                </motion.button>
              ))}
            </AnimatePresence>
          </div>
        </motion.div>
        {burst > 0 && <ConfettiBurst key={`burst-${burst}`} count={30} originY={45} reduceMotion={reduceMotion} />}
      </CountingPanel>

      <SupplyShelf label={`${noun} shelf`}>
        {supply.map((id) => {
          const packed = inBasket.includes(id);
          const isSel = selected === id;
          const dp = dragProps(id, ghost);
          return (
            <motion.div
              key={id}
              initial={reduceMotion ? false : { scale: 0, opacity: 0 }}
              animate={{ scale: packed ? 0.001 : 1, opacity: packed ? 0 : 1, y: isSel ? -8 : 0 }}
              transition={reduceMotion ? { duration: 0 } : { ...POP_SPRING, delay: packed ? 0 : Math.min(id * 0.05, 0.5) }}
              style={{ display: "inline-flex", width: packed ? 0 : undefined, overflow: packed ? "hidden" : "visible" }}
            >
              <button
                type="button"
                {...dp}
                aria-label={packed ? "Packed" : "Item to pack"}
                aria-hidden={packed ? "true" : undefined}
                tabIndex={packed ? -1 : undefined}
                className="counting-btn"
                disabled={done || packed}
                onClick={guardTap(() => {
                  if (done) return;
                  sfx?.("pop");
                  setSelected((s) => (s === id ? null : id));
                })}
                style={{ ...DRAG_BUTTON, ...dp.style, padding: 0, cursor: done ? "default" : "grab" }}
              >
                <CountingPiece asset={object} variant="bare" size={traySize} phase={id} floats={!isSel && !done} lifted={isSel} ring={isSel ? accent : undefined} reduceMotion={reduceMotion} style={{ borderRadius: "50%" }} />
              </button>
            </motion.div>
          );
        })}
      </SupplyShelf>
      <GhostLayer />
    </CountingRoot>
  );
};

export default GiveNGame;
